import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Datatable} from '../../../components/Datatable/Datatable'
import type {MetaType} from '../../../components/Datatable/Datatable'
import {Button} from '../../../components/Button/Button'
import useModals from '../../../hooks/useModals'
import {DELETE, POST} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {ModalDelete} from '../../../components/Modals/General/ModalDelete/ModalDelete'
import {useNavigate} from 'react-router-dom'
import Swal from 'sweetalert2'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {useSwitchStatus} from '../../../components/Datatable/SwitchStatus'

export enum TypeEntertainment {
  ENTERTAINMENT_EXPERIENCE = 'entertainment_experience',
  ENTERTAINMENT_ACTIVITY = 'entertainment_activity',
  ENTERTAINMENT_TOUR = 'entertainment_tour',
}

type EntertainmentServiceType = {
  id: number
  title: string
  type: TypeEntertainment
  status: boolean
  created: Date
  updated: Date
  typeLabel?: string
  statusLabel?: string
}

const getTypeLabel = (type: TypeEntertainment): string => {
  const labels = {
    [TypeEntertainment.ENTERTAINMENT_EXPERIENCE]: i18n.t('entertainment_services.type_experience'),
    [TypeEntertainment.ENTERTAINMENT_ACTIVITY]: i18n.t('entertainment_services.type_activity'),
    [TypeEntertainment.ENTERTAINMENT_TOUR]: i18n.t('entertainment_services.type_tour'),
  }
  return labels[type] || type
}

const EntertainmentServicesContent: FC = () => {
  const navigate = useNavigate()
  const {showDeleteModal} = useModals()
  const {switchStatus} = useSwitchStatus()
  const [loading, setLoading] = useState(false)
  const [services, setServices] = useState<EntertainmentServiceType[]>([])
  const [id, setId] = useState<number | null>(null)
  const [, setMeta] = useState<MetaType | undefined>(undefined)

  const getServices = async (filters?: {title?: string; status?: boolean}) => {
    try {
      setLoading(true)
      const res = await POST('/entertainment-service/filter', filters || {})
      const formattedServices = res.data.map((service: any) => ({
        ...service,
        typeLabel: getTypeLabel(service.type),
        statusLabel: service.status
          ? i18n.t('entertainment_services.status_active')
          : i18n.t('entertainment_services.status_inactive'),
      }))
      setServices(formattedServices)
      setMeta(res.meta)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
    }
  }

  const deleteService = async () => {
    try {
      setLoading(true)
      const response = await DELETE(`/entertainment-service/${id}`)
      if (response.success === true) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('entertainment_services.msg_success_delete')),
          text: String(i18n.t('entertainment_services.msg_success_delete_text')),
        }).then(async () => await getServices())
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('entertainment_services.msg_error_delete')),
          text: String(i18n.t('entertainment_services.msg_error_delete_text')),
        }).then(async () => await getServices())
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('entertainment_services.msg_error_delete')),
        text: String(i18n.t('entertainment_services.msg_error_delete_text')),
      }).then(async () => await getServices())
      console.log(e)
    }
  }

  useEffect(() => {
    getServices()
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('entertainment_services.list_title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('entertainment_services.list_subtitle')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t('entertainment_services.btn_add')}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('create')}
                  btnClass='mb-5 fw-bolder'
                />
              </div>
            </div>
            <div className='card-body'>
              <Datatable
                data={services}
                headers={[
                  {
                    key: i18n.t('entertainment_services.field_title'),
                    value: 'title',
                    type: 'default',
                  },
                  {
                    key: i18n.t('entertainment_services.field_type'),
                    value: 'typeLabel',
                    type: 'default',
                  },
                  {
                    key: i18n.t('general.active'),
                    value: 'status',
                    type: 'check',
                    action: (item) =>
                      switchStatus({
                        endpoint: 'entertainment-service',
                        Id: item.id,
                        status: item.status,
                      }),
                  },
                  {
                    key: i18n.t('entertainment_services.field_actions'),
                    value: 'acoes',
                    type: '',
                  },
                ]}
                options={[
                  {
                    icon: 'fas fa-pen',
                    action: (item) => {
                      navigate(`update/${item.id}`)
                    },
                    title: i18n.t('entertainment_services.btn_edit'),
                  },
                  {
                    icon: 'fas fa-trash',
                    action: (item) => {
                      showDeleteModal(i18n.t('entertainment_services.btn_delete'))
                      setId(item.id)
                    },
                    title: i18n.t('entertainment_services.btn_delete'),
                  },
                ]}
                color='primary'
              />
            </div>
          </div>

          <ModalDelete confirmAction={() => deleteService()} />
        </div>
      )}
    </>
  )
}

const EntertainmentServices: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: i18n.t('general.dashboard'), path: 'dashboard', isActive: true},
          {
            title: i18n.t('entertainment_services.list_title'),
            path: 'management/entertainment',
            isActive: true,
          },
        ]}
      >
        {i18n.t('entertainment_services.list_title')}
      </PageTitle>
      <EntertainmentServicesContent />
    </>
  )
}

export {EntertainmentServices}
